/**
 * `<page-job>` — one job, routed at `/jobs/:jobId`.
 *
 * Implements the read half of `SC-F332` / frozen interface `I7` (`06` §1.8) for a
 * single job: its type, its state, its progress, its actor, its typed result or
 * its typed error, and the one cancellation whose semantics depend on the type.
 *
 * Story: `SC-S322`.
 *
 * **This page is the durable permalink, and that is its whole design brief.**
 * Nine surfaces render an "Open job" link — the instance console twice, instance
 * creation, explain, tests, validation, the playground — and every one of them
 * *also* watches the job inline through `watchJob`. So nobody arrives here to
 * watch a job they just started; they arrive hours later, from a support thread or
 * an email, with a link and no context. Everything on the page follows from that:
 *
 *   - It reads the job **cold**. There is no handed-down state, no assumption that
 *     the job is still running, and no assumption that the reader was the actor.
 *   - A terminal job renders its whole outcome from the record alone, because the
 *     surface that watched it is long gone and its progress bar with it.
 *   - A `404` is answered with **retention**, not with "not found". Job rows are
 *     pruned after 30 days (90 for `policy.test` and `audit.verify`, which are
 *     evidence), so a link from an old thread pointing at nothing is the single
 *     most likely way this page is opened — and "that record does not exist" would
 *     read as a bug in the link rather than as the retention rule working.
 *   - It says where the outcome LIVES. A job is a receipt for something that
 *     happened somewhere else: a `config.publish` receipt belongs next to its
 *     generation, an `audit.export` receipt next to its download.
 *
 * It polls with [watchJob] and nothing else. `I7` is frozen precisely so the
 * product has one progress mechanism, and a second poll loop written here — even a
 * "better" one — would be the fifth.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  toastPersistent,
  type BnBadgeVariant,
  type BnKvRow,
} from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  JOB_STATUS_LABELS,
  JOB_TYPE_LABELS,
  cancelJob,
  getJob,
  isTerminal,
  jobActorLabel,
  jobFailure,
  jobRetentionDays,
  watchJob,
  type Job,
  type JobStatus,
  type JobType,
} from "../api/jobs.js";
import { getInstance, type Instance } from "../api/instances.js";
import type { ExportResult } from "../api/audit.js";
import type { PublishResult } from "../api/publish.js";
import { hrefFor, navigate } from "../nav.js";

/**
 * CANCELLABILITY is `06` §3.11's per-type cancellation contract, stated rather
 * than guessed.
 *
 * The distinction is not a nicety: the read-only proxied commands run in a
 * throwaway container and killing one loses nothing, while `config.publish`,
 * `config.rollback` and every `instance.*` type stop being cancellable the moment
 * the orchestrator begins mutating Swarm state — cancelling there would leave
 * desired and observed state divergent, and letting the reconcile loop finish is
 * strictly better than that. The route answers `409 job_not_cancellable` for the
 * second case, so this table decides only what the page SAYS in advance; the
 * server is the authority and its refusal is rendered verbatim when it comes.
 */
const CANCELLABILITY: Record<string, "always" | "while_queued"> = {
  "instance.create": "while_queued",
  "instance.delete": "while_queued",
  "instance.restart": "while_queued",
  "instance.suspend": "while_queued",
  "instance.resume": "while_queued",
  "instance.upgrade": "while_queued",
  "config.publish": "while_queued",
  "config.rollback": "while_queued",
  "config.validate": "always",
  "policy.validate": "always",
  "policy.explain": "always",
  "policy.test": "always",
  "datasource.check": "always",
  "datasource.probe": "always",
  "audit.verify": "always",
  "audit.export": "always",
  "schema.introspect": "always",
};

/**
 * WHAT_CANCELLING_LEAVES_BEHIND is the sentence a cancel dialog owes the reader,
 * per type.
 *
 * `SC-F332`'s done-when makes actually killing the underlying task this feature's
 * obligation and then adds, in the same breath, that **cancellation is not a
 * promise that nothing happened**. A cancelled writer job can leave a partially
 * written policy directory; a cancelled provision can leave objects the reconciler
 * has to sweep. Saying so before the button is pressed is the difference between a
 * cancel and an undo, and the product must never imply the second.
 */
const CANCEL_CONSEQUENCE: Record<string, string> = {
  "config.publish":
    "Only a publish that has not started yet can be cancelled. If it has begun, the generation stays in publishing until the instance confirms a digest or the attempt fails — the digest read-back, not this job's state, is what declares a generation live.",
  "config.rollback":
    "Only a rollback that has not started yet can be cancelled. A rollback publishes a new generation, so a cancelled one leaves the currently live generation exactly where it is.",
  "instance.create":
    "Only a provision that has not started yet can be cancelled. Once it has begun, the reconciler finishes or fails it, and the instance record says which — cancelling midway would leave cluster objects nothing owns.",
  "instance.delete":
    "Only a deletion that has not started yet can be cancelled. Once teardown has begun the record stays in deleting until every volume is confirmed reclaimed.",
};

/** The default consequence: a read-only proxied command. Nothing durable is being
 * written, so cancelling costs the run and nothing else. */
const CANCEL_CONSEQUENCE_DEFAULT =
  "This is a read-only command running in a throwaway container. Cancelling kills the container and loses the run's output; nothing on the instance changes, because nothing was being written.";

@customElement("page-job")
export class PageJob extends LitElement {
  /** The job record id, from the route. */
  @property() jobId = "";

  @state() private job: Job | null = null;
  @state() private loading = true;
  @state() private failure: ApiError | null = null;
  /** The job's instance, best-effort, so the page can name it rather than showing
   * a bare record id. A failure here is not a page failure. */
  @state() private instance: Instance | null = null;

  @state() private confirmCancel = false;
  @state() private cancelling = false;
  @state() private cancelFailure: ApiError | null = null;

  /** Aborts the in-flight watch on disconnect and before a re-watch. The job
   * keeps running server-side; only our observation of it stops. */
  private watchAbort?: AbortController;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 26rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .id {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    .muted {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    a.inline {
      color: var(--bino-primary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    a.inline:hover {
      text-decoration: underline;
    }
    a.inline:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    /* The in-flight row: a spinner, the step, the counter. The same shape the
       create wizard uses, deliberately — one progress vocabulary. */
    .progress {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .step {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .row {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-top: var(--bino-space-md);
    }
    .envelope {
      margin: 0 0 var(--bino-space-md);
      padding: var(--bino-space-sm) var(--bino-space-md);
      border: 1px solid var(--bino-error-border);
      border-radius: var(--bino-radius-sm);
      background: var(--bino-error-bg);
      color: var(--bino-text);
      font-size: var(--bino-font-size-md);
    }
    .envelope .code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    /* bn-code sizes to its host, so the host needs a height. */
    .result-doc {
      height: 18rem;
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.watchAbort?.abort();
    this.watchAbort = undefined;
  }

  /** Driven from `willUpdate` alone: the router sets `jobId` before the first
   * update, so loading from `connectedCallback` too would fetch twice. */
  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("jobId")) {
      this.watchAbort?.abort();
      this.watchAbort = undefined;
      this.job = null;
      this.instance = null;
      this.failure = null;
      this.cancelFailure = null;
      this.confirmCancel = false;
      if (this.jobId) void this.load();
    }
  }

  /**
   * load reads the job once, then watches it only if it is still running.
   *
   * The order matters for the cold open: the first render comes from a plain read,
   * so a terminal job — which is what almost every visit to this page finds — paints
   * immediately instead of after a long-poll window.
   */
  private async load() {
    this.loading = true;
    let job: Job;
    try {
      job = await getJob(this.jobId);
    } catch (err) {
      this.failure = parseError(err);
      this.job = null;
      this.loading = false;
      return;
    }
    this.job = job;
    this.failure = null;
    this.loading = false;
    void this.loadInstance(job);
    if (!isTerminal(job.status)) void this.watch();
  }

  /** The instance's name, so the page reads "acme-prod" rather than a record id.
   * Deliberately swallowed: the job is the subject of this page, and an instance
   * the caller can no longer read — a deleted one, most often — must not blank it. */
  private async loadInstance(job: Job) {
    if (!job.instance) return;
    try {
      this.instance = await getInstance(job.instance);
    } catch {
      this.instance = null;
    }
  }

  /**
   * watch follows a running job to its terminal state with the product's one
   * progress helper.
   *
   * [watchJob] resolves on **every** terminal state rather than rejecting on
   * failure, which is why there is no error branch for a failed job here: a failed
   * publish is an outcome this page renders, not an exception. Only the watch
   * itself breaking lands in the catch, and then the record on screen is still the
   * last true observation — so the page keeps it and offers a refresh rather than
   * replacing a real job with a transport error.
   */
  private async watch() {
    this.watchAbort?.abort();
    this.watchAbort = new AbortController();
    try {
      const done = await watchJob(this.jobId, {
        onUpdate: (job) => (this.job = job),
        signal: this.watchAbort.signal,
      });
      this.job = done;
      const failed = jobFailure(done);
      if (failed) {
        toastPersistent(failed.message, "error", `${label(done.type)} failed`);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      // Not a page failure: the job is the server's, the watch was ours.
      this.failure = null;
      toast(parseError(err).message, "warning");
    }
  }

  /* ── cancellation (`SC-F332`, `06` §3.11) ────────────────────────────── */

  private async runCancel() {
    this.cancelling = true;
    this.cancelFailure = null;
    try {
      await cancelJob(this.jobId);
      this.confirmCancel = false;
      // The route answers the cancelled record, but re-reading is what keeps this
      // page's single source of truth the same one a cold open uses.
      await this.load();
      toast("Cancellation requested.", "success");
    } catch (err) {
      this.cancelFailure = parseError(err);
    } finally {
      this.cancelling = false;
    }
  }

  /* ── rendering ───────────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  /** The identity block. Every row here is something a support conversation asks
   * for, which is why the ids are present as values rather than only as links. */
  private identityRows(job: Job): BnKvRow[] {
    const rows: BnKvRow[] = [
      { label: "Job id", value: job.id },
      { label: "Type", value: `${label(job.type)} (${job.type})` },
      { label: "Status", value: JOB_STATUS_LABELS[job.status] ?? job.status },
      { label: "Started by", value: jobActorLabel(job.actor) },
      { label: "Organization id", value: job.org },
    ];
    if (job.instance) {
      rows.push({
        label: "Instance",
        value: this.instance ? `${this.instance.name} (${job.instance})` : job.instance,
      });
    }
    rows.push({ label: "Created", value: when(job.created) });
    if (job.started) rows.push({ label: "Started", value: when(job.started) });
    if (job.finished) {
      rows.push({ label: "Finished", value: when(job.finished) });
      rows.push({ label: "Took", value: elapsed(job.started ?? job.created, job.finished) });
    }
    if (job.idempotency_key) {
      rows.push({ label: "Idempotency key", value: job.idempotency_key });
    }
    return rows;
  }

  /** The in-flight state. `queued` and `running` are two different sentences: one
   * is waiting for a worker, the other is doing the work. */
  private renderInFlight(job: Job): TemplateResult | typeof nothing {
    if (isTerminal(job.status)) return nothing;
    const p = job.progress;
    return html`<bn-card heading="In progress">
      <div class="progress">
        <bn-spinner size="28"></bn-spinner>
        <div role="status" aria-live="polite">
          <div>${p ? p.step : "Waiting for a worker to pick the job up"}</div>
          <div class="step">
            ${p
              ? `Step ${p.index} of ${p.total}`
              : `Job ${JOB_STATUS_LABELS[job.status] ?? job.status}`}
          </div>
        </div>
      </div>
      <p class="copy">
        This page is following the job, and so is whatever started it — the same job object, watched
        twice. Closing this tab does not stop the work: it runs in the control plane, not in your
        session.
      </p>
      ${this.renderCancelControl(job)}
    </bn-card>`;
  }

  /** The cancel affordance, with the type's own semantics stated next to it rather
   * than discovered through a 409. */
  private renderCancelControl(job: Job): TemplateResult {
    const rule = CANCELLABILITY[job.type] ?? "always";
    const onlyQueued = rule === "while_queued";
    const offerable = !onlyQueued || job.status === "queued";
    return html`
      <div class="actions">
        <bn-button
          ?disabled=${!offerable || this.cancelling}
          @click=${() => {
            this.cancelFailure = null;
            this.confirmCancel = true;
          }}
        >
          Cancel this job
        </bn-button>
      </div>
      ${onlyQueued
        ? html`<p class="muted" style="margin-top: var(--bino-space-sm)">
            ${offerable
              ? "This type is cancellable only while it is queued. Once the orchestrator starts changing cluster state, cancelling would leave desired and observed state divergent, so the answer becomes a refusal."
              : "This type is no longer cancellable: the orchestrator has begun changing cluster state, and stopping midway would leave desired and observed state divergent. Letting the reconcile loop finish is the safer outcome."}
          </p>`
        : nothing}
      ${this.cancelFailure
        ? html`<div role="alert" style="margin-top: var(--bino-space-sm)">
            <p class="copy">${this.cancelFailure.message}</p>
            <bn-kv .rows=${this.supportRows(this.cancelFailure)}></bn-kv>
          </div>`
        : nothing}
    `;
  }

  private renderCancelDialog(job: Job): TemplateResult | typeof nothing {
    if (!this.confirmCancel) return nothing;
    return html`<bn-modal
      open
      tone="destructive"
      .width=${560}
      heading=${`Cancel this ${label(job.type).toLowerCase()}?`}
      @close=${() => (this.confirmCancel = false)}
    >
      <p class="copy">
        Cancelling asks the control plane to kill the underlying task, and it does kill it — that is
        this feature's obligation and not the substrate's.
      </p>
      <p slot="consequence">
        <strong>Cancelling is not a promise that nothing happened.</strong>
        ${CANCEL_CONSEQUENCE[job.type] ?? CANCEL_CONSEQUENCE_DEFAULT}
      </p>
      <bn-button slot="footer" @click=${() => (this.confirmCancel = false)}>Keep running</bn-button>
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.cancelling}
        @click=${() => void this.runCancel()}
      >
        Cancel the job
      </bn-button>
    </bn-modal>`;
  }

  /**
   * The failure block. It renders the job's stored envelope, which is `06` §1.4's
   * envelope **minus `request_id`** — and says so, because the absence looks like a
   * bug otherwise. A job outlives the request that created it, so the id of that
   * request would point at the submission rather than at the failure, which is
   * worse than having no id at all.
   */
  private renderOutcomeFailure(job: Job): TemplateResult | typeof nothing {
    if (job.status === "succeeded" || !isTerminal(job.status)) return nothing;
    const failed = jobFailure(job);
    const details = job.error?.details;
    return html`<bn-card heading=${`${label(job.type)} — ${JOB_STATUS_LABELS[job.status] ?? job.status}`}>
      <div role="alert">
        <div class="envelope">
          <p style="margin: 0 0 var(--bino-space-xs)">${failed?.message ?? "The operation failed."}</p>
          ${job.error ? html`<span class="code">${job.error.code}</span>` : nothing}
        </div>
      </div>
      ${details && Object.keys(details).length > 0
        ? html`<div class="result-doc">
            <bn-code
              language="plain"
              label="Failure details"
              filename="job-error.json"
              .value=${pretty(details)}
            ></bn-code>
          </div>`
        : nothing}
      <p class="muted" style="margin-top: var(--bino-space-md)">
        A job's stored failure carries the error code, the message and the details, and deliberately
        <strong>no request id</strong>: a job outlives the request that created it, so that id would
        name the submission rather than the failure. Quote the job id above instead — it is the
        correlation key for everything this job did.
      </p>
      ${job.status === "timed_out"
        ? html`<p class="muted">
            Timed out means the reaper found the job with no live worker behind it and released the
            instance lock. That release is the point: without it, one dead worker would lock an
            instance out of every publish and every lifecycle action indefinitely.
          </p>`
        : nothing}
      ${job.status === "cancelled"
        ? html`<p class="muted">
            Cancelled is an outcome somebody chose, not a failure — and not an undo. See the
            operation's own surface for what it left behind.
          </p>`
        : nothing}
    </bn-card>`;
  }

  /**
   * The success block.
   *
   * Two result shapes are rendered structurally because the client already types
   * them and because both carry a fact that must not be buried: a publish's two
   * digests, whose *equality* is the definition of success (`SC-F608`), and an
   * export's signed URL, which expires. Everything else is shown as its raw result
   * document — honest for a shape this bundle does not model, and still the whole
   * receipt.
   */
  private renderOutcomeSuccess(job: Job): TemplateResult | typeof nothing {
    if (job.status !== "succeeded") return nothing;
    return html`<bn-card heading=${`${label(job.type)} — succeeded`}>
      ${this.renderTypedResult(job)}
      ${this.renderWhereToLook(job)}
      ${job.result !== null && job.result !== undefined
        ? html`<div class="result-doc" style="margin-top: var(--bino-space-md)">
            <bn-code
              language="plain"
              label="Result"
              filename=${`job-${job.id}-result.json`}
              .value=${pretty(job.result)}
            ></bn-code>
          </div>`
        : html`<p class="copy">
            This job type reports no result document. It succeeded, and the change it made is
            visible on the surface it belongs to.
          </p>`}
    </bn-card>`;
  }

  private renderTypedResult(job: Job): TemplateResult | typeof nothing {
    if (job.type === "config.publish" || job.type === "config.rollback") {
      const r = job.result as PublishResult | null;
      if (!r) return nothing;
      const confirmed = r.rendered_digest === r.observed_digest;
      return html`
        <bn-kv
          .rows=${[
            { label: "Generation", value: String(r.generation) },
            { label: "Rendered digest", value: r.rendered_digest },
            { label: "Digest the instance reported", value: r.observed_digest },
            { label: "Path", value: r.path },
            {
              label: "Measured unavailability",
              value:
                r.downtime_ms === null
                  ? undefined
                  : r.downtime_ms === 0
                    ? "0 ms — the hot-reload path replaces no task"
                    : `${r.downtime_ms} ms`,
            },
          ] as BnKvRow[]}
          unsetText="not measured"
        ></bn-kv>
        <p class="copy" style="margin-top: var(--bino-space-md)">
          ${confirmed
            ? html`The instance read back <strong>the same digest</strong> the control plane
                rendered. That equality is the entire definition of a successful publish here —
                "we sent it" is not "it is running".`
            : html`The digests <strong>differ</strong>, which means the instance is not running what
                was rendered. The generation is not live on the strength of this job.`}
        </p>
        ${r.downtime_ms === null && r.path !== "hot_reload"
          ? html`<p class="muted">
              Unavailability is reported as not measured rather than as none: the rollout did not
              return one, and filling it in from the wall clock would include readiness polling and
              overstate the outage.
            </p>`
          : nothing}
        ${r.unhealthy_after && r.unhealthy_after.length > 0
          ? html`<p class="muted">
              Datasources unhealthy after the change: ${r.unhealthy_after.join(", ")}.
              ${r.unhealthy_before && r.unhealthy_before.length > 0
                ? `Already unhealthy before it: ${r.unhealthy_before.join(", ")}.`
                : ""}
            </p>`
          : nothing}
      `;
    }
    if (job.type === "audit.export") {
      const r = job.result as ExportResult | null;
      if (!r) return nothing;
      const expired = r.expires ? new Date(r.expires).getTime() < Date.now() : false;
      return html`
        <bn-kv
          .rows=${[
            { label: "Records", value: r.records === undefined ? undefined : String(r.records) },
            { label: "Bytes", value: r.bytes === undefined ? undefined : String(r.bytes) },
            { label: "Content type", value: r.content_type },
            { label: "Link expires", value: r.expires ? when(r.expires) : undefined },
          ] as BnKvRow[]}
          unsetText="not reported"
        ></bn-kv>
        ${r.url && !expired
          ? html`<div class="actions">
              <bn-button variant="primary" @click=${() => window.open(r.url, "_blank", "noopener")}>
                Download the export
              </bn-button>
            </div>`
          : html`<p class="copy" style="margin-top: var(--bino-space-md)">
              ${expired
                ? "The signed download link has expired. Request the export again — the link is deliberately short-lived, because it carries a bulk copy of the organization's query history and needs no credential to follow."
                : "This export reported no download link."}
            </p>`}
      `;
    }
    return nothing;
  }

  /**
   * "Where to look" — the one thing a permalink owes a reader who arrived with no
   * context. A job is a receipt; the thing it is a receipt for lives elsewhere.
   */
  private renderWhereToLook(job: Job): TemplateResult | typeof nothing {
    const instanceId = job.instance;
    if (!instanceId) return nothing;
    const to = (...segments: Array<string | number>) =>
      hrefFor("instances", instanceId, ...segments);
    switch (job.type) {
      case "config.publish":
      case "config.rollback": {
        const r = job.result as PublishResult | null;
        return html`<p class="copy">
          The generation this produced, with its attribution and its rendered bundle, is on
          <a class="inline" href=${r ? to("generations", r.generation) : to("generations")}
            >${r ? `generation ${r.generation}` : "the generation history"}</a
          >.
        </p>`;
      }
      case "policy.test":
        return html`<p class="copy">
          The full report, case by case, is on
          <a class="inline" href=${to("tests")}>the tests surface</a>. Test runs are retained for 90
          days rather than 30, because a passing suite is evidence.
        </p>`;
      case "config.validate":
      case "policy.validate":
        return html`<p class="copy">
          Findings, anchored to the objects that caused them, are on
          <a class="inline" href=${to("validation")}>the validation surface</a>.
        </p>`;
      case "policy.explain":
        return html`<p class="copy">
          The decision walk-through is on
          <a class="inline" href=${to("explain")}>the explain surface</a>.
        </p>`;
      case "audit.verify":
        return html`<p class="copy">
          The chain's state is on <a class="inline" href=${to("audit")}>the audit surface</a>.
          Verifications are retained for 90 days rather than 30, because they are evidence.
        </p>`;
      case "audit.export":
        return html`<p class="copy">
          Exports are requested from <a class="inline" href=${to("audit")}>the audit surface</a>.
        </p>`;
      case "datasource.check":
      case "datasource.probe":
      case "schema.introspect":
        return html`<p class="copy">
          Datasource state and schema freshness are on
          <a class="inline" href=${to("datasources")}>the datasources surface</a>.
        </p>`;
      default:
        return html`<p class="copy">
          The instance's own state, its timeline and its recent operations are on
          <a class="inline" href=${to()}>the instance console</a>.
        </p>`;
    }
  }

  /**
   * The `404`, answered with retention rather than with absence.
   *
   * This is the most likely single outcome of opening a link from an old thread,
   * and it is also the one where "that record does not exist, or you do not have
   * access to it" — the catalogue's own copy for `not_found` — reads as a broken
   * product. Both halves of that sentence are true and the reader cannot tell which
   * applies, so the page names both and puts the retention rule first, because it
   * is by far the commoner cause.
   */
  private renderMissing(): TemplateResult {
    return html`
      <a class="back" href="/instances">← Instances</a>
      <bn-empty-state
        icon="◌"
        heading="This job is no longer here"
        message="Finished jobs are pruned after 30 days — 90 for policy test runs and audit chain verifications, which are kept as evidence. A link older than that points at a record that has been retired on purpose. It is also possible the job belongs to an organization you are not a member of: the API answers the same 404 for both, deliberately, so that a job id cannot be used to probe another organization."
      >
        <bn-button slot="actions" variant="primary" @click=${() => navigate("/instances")}>
          Go to instances
        </bn-button>
      </bn-empty-state>
      <bn-panel eyebrow="What is still readable">
        <p class="copy">
          What a job did outlives the job row. A publish leaves a numbered generation with its
          attribution and its failure reason; a lifecycle action leaves an entry on the instance's
          timeline; both are retained far longer than the job that carried them. Open the instance
          and read its Operations panel and its generation history.
        </p>
      </bn-panel>
    `;
  }

  override render() {
    if (this.loading && !this.job) {
      return html`<bn-spinner block size="28" label="Loading the job"></bn-spinner>`;
    }
    if (this.failure) {
      if (this.failure.code === "not_found") return this.renderMissing();
      return html`
        <a class="back" href="/instances">← Instances</a>
        <bn-card heading="This job could not be read">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            ${this.failure.code === "insufficient_scope"
              ? html`<p>
                  A job inherits the feature of the route that created it — a publish needs
                  <code>policies</code>, a provision needs <code>instances</code>, a test needs
                  <code>validation</code> — so an API key narrowed to one feature cannot watch a job
                  it could never have started.
                </p>`
              : nothing}
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
            </div>
          </div>
        </bn-card>
      `;
    }
    const job = this.job;
    if (!job) {
      return html`<bn-empty-state
        icon="∅"
        heading="No job"
        message="This page was opened without a job id."
      ></bn-empty-state>`;
    }

    const backHref = job.instance ? hrefFor("instances", job.instance) : "/instances";
    const backLabel = job.instance ? "← Instance" : "← Instances";
    return html`
      <a class="back" href=${backHref}>${backLabel}</a>
      <header>
        <div class="head-text">
          <h1>
            ${label(job.type)}
            <bn-badge variant=${statusVariant(job.status)} ?dot=${!isTerminal(job.status)}>
              ${JOB_STATUS_LABELS[job.status] ?? job.status}
            </bn-badge>
          </h1>
          <p class="lede">
            Every operation in Sluice Cloud that cannot finish inside one request is this same
            object — a provision, a publish, a roll-forward, a restart, a proxied command, an
            export. One shape, one polling contract, one place to read what happened.
          </p>
          <p class="id">${job.id}</p>
        </div>
        <div>
          <bn-button @click=${() => void this.load()}>Refresh</bn-button>
        </div>
      </header>

      <div class="stack">
        ${this.renderInFlight(job)} ${this.renderOutcomeFailure(job)}
        ${this.renderOutcomeSuccess(job)}
        <bn-card heading="Job record">
          <bn-kv .rows=${this.identityRows(job)}></bn-kv>
          ${
            // Only when the success block did not already say it. A succeeded job
            // gets the pointer next to its result, where it belongs; a failed,
            // cancelled or timed-out one has no result block to hang it off, and
            // "where do I go to see the state this left behind" is exactly the
            // question a failure raises.
            isTerminal(job.status) && job.status !== "succeeded"
              ? this.renderWhereToLook(job)
              : nothing
          }
          <p class="muted" style="margin-top: var(--bino-space-md)">
            Retained for ${jobRetentionDays(job.type)} days after it finished.
            ${jobRetentionDays(job.type) > 30
              ? "This type is kept longer than the others because its output is evidence."
              : "The record it produced — a generation, a timeline entry — is kept longer."}
          </p>
        </bn-card>
        ${isTerminal(job.status) && job.instance
          ? html`<bn-panel eyebrow="This instance's other operations">
              <p class="copy">
                Provisioning, publishing, restarts, deletions and proxied commands are all the same
                object, so they are all in one list:
                <a class="inline" href=${hrefFor("instances", job.instance, "jobs")}
                  >the instance's job history</a
                >.
              </p>
            </bn-panel>`
          : nothing}
      </div>
      ${this.renderCancelDialog(job)}
    `;
  }
}

/** label is the job type's display word, falling back to the dotted token itself
 * for a type this bundle does not know — a newer server, which `06` §1.3 permits
 * as an additive change in a response position. */
function label(type: JobType | string): string {
  return JOB_TYPE_LABELS[type] ?? type;
}

/** Job status → badge tone. `cancelled` is neutral: a cancelled job is an outcome
 * the reader chose, not a failure. */
function statusVariant(status: JobStatus | string): BnBadgeVariant {
  switch (status) {
    case "succeeded":
      return "success";
    case "failed":
    case "timed_out":
      return "error";
    case "running":
      return "info";
    default:
      return "neutral";
  }
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** elapsed renders a duration between two timestamps, which for a permalink is
 * more useful than either timestamp alone: "took 41 s" is the sentence somebody
 * reading a support thread wants. */
function elapsed(from: string, to: string): string {
  const a = new Date(from).getTime();
  const b = new Date(to).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return "—";
  const ms = b - a;
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 90) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  return `${m} min ${Math.round(s - m * 60)} s`;
}

/** pretty renders a result or a details object as indented JSON for `bn-code`. A
 * value that cannot be serialized is reported as such rather than swallowed: a
 * blank result panel on a succeeded job reads as a product that lost the receipt. */
function pretty(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return "// this result could not be rendered as JSON";
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-job": PageJob;
  }
}
